/**
 * Qore AI — floating assistant (all authenticated pages)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Brain } from 'lucide-react';
import './AITutor.css';
import '../shared/QoreAI.css';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ChatHeader from './ChatHeader';
import TypingIndicator from './TypingIndicator';
import QoreAIWelcome from '../shared/QoreAIWelcome';
import useQoreAIChat from '../../../hooks/useQoreAIChat';

const AITutor = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connectionStatus] = useState('ready');
  const [windowSize, setWindowSize] = useState({ width: 440, height: 680 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);

  const {
    messages,
    isAITyping,
    sendMessage,
    newConversation,
    retryLast,
    context,
    refreshContext,
  } = useQoreAIChat();

  const windowRef = useRef(null);
  const resizeStartPos = useRef({ x: 0, y: 0 });
  const resizeStartSize = useRef({ width: 0, height: 0 });
  const prevMsgCount = useRef(0);

  useEffect(() => {
    const saved = localStorage.getItem('aiTutorWindowSize');
    if (saved) {
      try {
        setWindowSize(JSON.parse(saved));
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    if (!isOpen && messages.length > prevMsgCount.current && prevMsgCount.current > 0) {
      const last = messages[messages.length - 1];
      if (last?.role === 'assistant') setUnreadCount((c) => c + 1);
    }
    prevMsgCount.current = messages.length;
  }, [messages, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) refreshContext();
  }, [isOpen, refreshContext, window.location.pathname]);

  const handleResizeStart = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStartPos.current = { x: e.clientX, y: e.clientY };
    resizeStartSize.current = { ...windowSize };
  };

  useEffect(() => {
    if (!isResizing) return undefined;

    const onMove = (e) => {
      const deltaX = resizeStartPos.current.x - e.clientX;
      const deltaY = e.clientY - resizeStartPos.current.y;
      let w = resizeStartSize.current.width;
      let h = resizeStartSize.current.height;
      const minW = 320;
      const maxW = window.innerWidth - 40;
      const minH = 400;
      const maxH = window.innerHeight - 40;

      if (resizeDirection?.includes('left')) {
        w = Math.min(Math.max(resizeStartSize.current.width + deltaX, minW), maxW);
      }
      if (resizeDirection?.includes('top')) {
        h = Math.min(Math.max(resizeStartSize.current.height - deltaY, minH), maxH);
      }
      if (resizeDirection?.includes('bottom')) {
        h = Math.min(Math.max(resizeStartSize.current.height + deltaY, minH), maxH);
      }
      setWindowSize({ width: w, height: h });
    };

    const onUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
      setWindowSize((s) => {
        localStorage.setItem('aiTutorWindowSize', JSON.stringify(s));
        return s;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isResizing, resizeDirection]);

  const handleToggle = () => {
    setIsOpen((o) => !o);
    if (!isOpen) setUnreadCount(0);
  };

  const handleSendMessage = async (content, attachments = []) => {
    if (attachments?.length) {
      await sendMessage(`${content}\n[${attachments.length} attachment(s)]`.trim());
    } else {
      await sendMessage(content);
    }
  };

  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) return null;

  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;

  return (
    <>
      {!isOpen && (
        <button
          className="ai-tutor-fab"
          onClick={handleToggle}
          aria-label="Open Qore AI"
          type="button"
        >
          <span className="qai-fab-brand">
            <Brain strokeWidth={2.25} />
            <span className="qai-fab-label">AI</span>
          </span>
          {unreadCount > 0 && (
            <span className="ai-tutor-fab-badge" aria-live="polite">{unreadCount}</span>
          )}
        </button>
      )}

      {isOpen && (
        <div
          ref={windowRef}
          className={`ai-tutor-window ${isMinimized ? 'minimized' : ''} ${isResizing ? 'resizing' : ''}`}
          style={isDesktop ? {
            width: `${windowSize.width}px`,
            height: isMinimized ? 'auto' : `${windowSize.height}px`,
          } : undefined}
          role="dialog"
          aria-modal="true"
          aria-label="Qore AI"
        >
          {!isMinimized && isDesktop && (
            <>
              <div className="resize-handle resize-top" onMouseDown={(e) => handleResizeStart(e, 'top')} role="presentation" />
              <div className="resize-handle resize-left" onMouseDown={(e) => handleResizeStart(e, 'left')} role="presentation" />
              <div className="resize-handle resize-bottom" onMouseDown={(e) => handleResizeStart(e, 'bottom')} role="presentation" />
              <div className="resize-handle resize-bottom-left" onMouseDown={(e) => handleResizeStart(e, 'bottom-left')} role="presentation" />
            </>
          )}

          <ChatHeader
            onClose={handleToggle}
            onMinimize={() => setIsMinimized((m) => !m)}
            onNewChat={newConversation}
            connectionStatus={connectionStatus}
            isMinimized={isMinimized}
            context={context}
          />

          {!isMinimized && (
            <div className="ai-tutor-body">
              {messages.length === 0 ? (
                <QoreAIWelcome
                  variant="widget"
                  showExpandLink
                  onPrompt={handleSendMessage}
                />
              ) : (
                <>
                  <MessageList
                    messages={messages}
                    onRetry={retryLast}
                    showScrollFab
                  />
                  {isAITyping && <TypingIndicator />}
                </>
              )}

              <MessageInput
                onSend={handleSendMessage}
                disabled={false}
                isSending={isAITyping}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AITutor;